/* ============================================================================
 * SYLONI® · Hero · Particle Convergence (Sprint 1)
 *
 * Production-grade particle physics for the hero section.
 *
 * Features:
 *   - Auto-discovers all [data-syloni-hero-particle] elements
 *   - Reads config from data-* attributes (set by Liquid section)
 *   - Particle count auto-scales by viewport breakpoint
 *   - Mouse repulsion (configurable strength)
 *   - Pauses when not in viewport (battery / CPU saver)
 *   - Disabled on Reduced Motion / low-end devices (CSS handles fallback)
 *   - Disabled on touch-primary devices (mouse interaction makes no sense)
 *   - Multiple instances supported
 *   - Theme editor compatible (re-init on shopify:section:load event)
 *   - DPR-aware rendering (sharp on retina)
 *   - Cleanup on disconnect (no memory leaks)
 *
 * Depends on: assets/syloni-motion-base.js (for window.SYLONI globals)
 *
 * Last update: 2026-04-27 · Sprint 1 v0.2.0
 * ============================================================================ */

(function () {
  'use strict';

  const SELECTOR = '[data-syloni-hero-particle]';
  const instances = new WeakMap();

  // ───────────────────────────────────────────────────────────────
  // ParticleHero · single-instance controller
  // ───────────────────────────────────────────────────────────────
  class ParticleHero {
    constructor(el) {
      this.el = el;
      this.canvas = el.querySelector('.syloni-hero-particle__canvas');
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d', { alpha: true });

      // Read config
      this.cfg = this._readConfig(el);

      // State
      this.particles = [];
      this.mouse = { x: -9999, y: -9999, active: false };
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.w = 0;
      this.h = 0;
      this.rafId = null;
      this.visible = true;

      // Bound handlers (for clean removal)
      this._boundResize = this._handleResize.bind(this);
      this._boundMove = this._handleMouseMove.bind(this);
      this._boundLeave = this._handleMouseLeave.bind(this);

      this._init();
    }

    _readConfig(el) {
      const ds = el.dataset;
      const isMobile = window.matchMedia('(max-width: 640px)').matches;
      const isTablet = window.matchMedia('(max-width: 1024px)').matches && !isMobile;

      let count;
      if (isMobile) count = parseInt(ds.particlesMobile, 10) || 60;
      else if (isTablet) count = parseInt(ds.particlesTablet, 10) || 90;
      else count = parseInt(ds.particlesDesktop, 10) || 140;

      return {
        count,
        mouseStrength: parseFloat(ds.mouseStrength) || 0.35,
        mouseRadius: 180,
        colors: [
          ds.color1 || '#D4AF37',
          ds.color1 || '#D4AF37', // weight primary 2x
          ds.color2 || '#E8C865',
          ds.color2 || '#E8C865',
          ds.color3 || '#FFE4A0',
          ds.color4 || '#0E7A82', // accent 1x only
        ],
      };
    }

    _init() {
      // Skip if reduced motion or low-end device
      const html = document.documentElement;
      if (
        html.classList.contains('syloni-no-motion') ||
        html.classList.contains('syloni-touch-device')
      ) {
        return;
      }

      this._handleResize();
      this._spawnParticles();

      this.canvas.addEventListener('mousemove', this._boundMove);
      this.canvas.addEventListener('mouseleave', this._boundLeave);
      window.addEventListener('resize', this._boundResize);

      // Pause when scrolled out of view
      if ('IntersectionObserver' in window) {
        this.viewObs = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              this.visible = e.isIntersecting;
              if (this.visible && !this.rafId && !document.hidden) this._tick();
            });
          },
          { threshold: 0.05 }
        );
        this.viewObs.observe(this.el);
      }

      // Pause when tab hidden (battery / CPU saver)
      this._boundVisibility = () => {
        if (document.hidden) {
          if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
          }
        } else if (this.visible && !this.rafId) {
          this._tick();
        }
      };
      document.addEventListener('visibilitychange', this._boundVisibility);

      this._tick();
    }

    _handleResize() {
      const r = this.canvas.getBoundingClientRect();
      this.w = r.width;
      this.h = r.height;
      this.canvas.width = Math.round(this.w * this.dpr);
      this.canvas.height = Math.round(this.h * this.dpr);
      this.canvas.style.width = this.w + 'px';
      this.canvas.style.height = this.h + 'px';
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }

    _handleMouseMove(e) {
      const r = this.canvas.getBoundingClientRect();
      this.mouse.x = e.clientX - r.left;
      this.mouse.y = e.clientY - r.top;
      this.mouse.active = true;
    }

    _handleMouseLeave() {
      this.mouse.x = -9999;
      this.mouse.y = -9999;
      this.mouse.active = false;
    }

    _spawnParticles() {
      this.particles = [];
      const { count, colors } = this.cfg;
      for (let i = 0; i < count; i++) {
        this.particles.push({
          x: Math.random() * this.w,
          y: Math.random() * this.h,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          r: Math.random() * 1.6 + 0.8,
          color: colors[Math.floor(Math.random() * colors.length)],
          alpha: Math.random() * 0.5 + 0.5,
          twinkle: Math.random() * Math.PI * 2,
        });
      }
    }

    _tick() {
      if (!this.visible) {
        this.rafId = null;
        return;
      }

      this.ctx.clearRect(0, 0, this.w, this.h);
      const { mouseStrength, mouseRadius } = this.cfg;
      const mx = this.mouse.x;
      const my = this.mouse.y;

      for (let i = 0; i < this.particles.length; i++) {
        const p = this.particles[i];

        // Mouse repulsion (only when active and within radius)
        if (this.mouse.active) {
          const dx = p.x - mx;
          const dy = p.y - my;
          const d2 = dx * dx + dy * dy;
          if (d2 < mouseRadius * mouseRadius) {
            const f = (1 - Math.sqrt(d2) / mouseRadius) * mouseStrength;
            p.vx += (dx / Math.max(Math.sqrt(d2), 1)) * f;
            p.vy += (dy / Math.max(Math.sqrt(d2), 1)) * f;
          }
        }

        // Damping
        p.vx *= 0.97;
        p.vy *= 0.97;

        // Drift (subtle ambient motion when no mouse)
        p.vx += (Math.random() - 0.5) * 0.01;
        p.vy += (Math.random() - 0.5) * 0.01;

        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Wall bounce (soft)
        if (p.x < 0) { p.x = 0; p.vx *= -0.7; }
        if (p.x > this.w) { p.x = this.w; p.vx *= -0.7; }
        if (p.y < 0) { p.y = 0; p.vy *= -0.7; }
        if (p.y > this.h) { p.y = this.h; p.vy *= -0.7; }

        // Twinkle (sine modulated alpha)
        p.twinkle += 0.02;
        const twinkleAlpha = p.alpha * (0.7 + 0.3 * Math.sin(p.twinkle));

        // Draw with glow
        this.ctx.save();
        this.ctx.shadowColor = p.color;
        this.ctx.shadowBlur = 6;
        this.ctx.fillStyle = this._withAlpha(p.color, twinkleAlpha);
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.restore();
      }

      this.rafId = requestAnimationFrame(this._tick.bind(this));
    }

    _withAlpha(color, alpha) {
      // Convert #RRGGBB → rgba()
      if (color.startsWith('#')) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r},${g},${b},${alpha})`;
      }
      return color;
    }

    destroy() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.viewObs) this.viewObs.disconnect();
      if (this._boundVisibility) {
        document.removeEventListener('visibilitychange', this._boundVisibility);
      }
      this.canvas.removeEventListener('mousemove', this._boundMove);
      this.canvas.removeEventListener('mouseleave', this._boundLeave);
      window.removeEventListener('resize', this._boundResize);
      this.particles = [];
      this.ctx.clearRect(0, 0, this.w, this.h);
    }
  }

  // ───────────────────────────────────────────────────────────────
  // Auto-init on DOM ready + theme editor live re-init
  // ───────────────────────────────────────────────────────────────
  function initAll(scope) {
    const root = scope || document;
    const els = root.querySelectorAll(SELECTOR);
    els.forEach((el) => {
      if (instances.has(el)) return;
      const inst = new ParticleHero(el);
      instances.set(el, inst);
    });
  }

  function destroyEl(el) {
    const inst = instances.get(el);
    if (inst) {
      inst.destroy();
      instances.delete(el);
    }
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Shopify theme editor lifecycle hooks
  // (These fire when an admin edits/reloads a section in theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector) {
      initAll(e.target);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    if (e.target) {
      const els = e.target.querySelectorAll
        ? e.target.querySelectorAll(SELECTOR)
        : [];
      els.forEach(destroyEl);
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    // Re-scan in case order change affects visibility
    initAll();
  });

  // Expose to window.SYLONI for debugging
  if (window.SYLONI) {
    window.SYLONI.modules.heroParticle = {
      version: '0.2.0',
      initAll,
      destroyEl,
      instances,
    };
  }

  console.log(
    '%c SYLONI hero-particle v0.2.0 ',
    'background:#D4AF37;color:#0A0A0A;padding:3px 8px;font-family:monospace;letter-spacing:2px;font-weight:600;'
  );
})();
